import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

function subscribeReducedMotion(callback) {
  const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
  mq.addEventListener("change", callback);
  return () => mq.removeEventListener("change", callback);
}

function getReducedMotionSnapshot() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Typewriter cycle that fills a fixed parent box.
 * Does not change parent size. Longer phrases scale just enough to stay inside.
 */
export default function TypewriterText({
  texts = [],
  typingSpeed = 85,
  deletingSpeed = 45,
  pauseDuration = 2000,
  className = "",
  style,
  showCursor = true,
  cursorClassName = "typewriter-cursor",
}) {
  const prefersReducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    getReducedMotionSnapshot,
    () => true,
  );
  const [textIndex, setTextIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [scale, setScale] = useState(1);
  const clipRef = useRef(null);
  const probeRef = useRef(null);

  const safeTexts = useMemo(
    () => (Array.isArray(texts) ? texts.filter(Boolean) : []),
    [texts],
  );

  const currentWord = safeTexts[textIndex % Math.max(safeTexts.length, 1)] || "";

  useEffect(() => {
    if (prefersReducedMotion || safeTexts.length === 0) return undefined;

    let timeoutId;

    if (!isDeleting && displayText === currentWord) {
      timeoutId = setTimeout(() => setIsDeleting(true), pauseDuration);
    } else if (isDeleting && displayText === "") {
      timeoutId = setTimeout(() => {
        setIsDeleting(false);
        setTextIndex((prev) => (prev + 1) % safeTexts.length);
      }, deletingSpeed);
    } else if (isDeleting) {
      timeoutId = setTimeout(() => {
        setDisplayText(currentWord.substring(0, displayText.length - 1));
      }, deletingSpeed);
    } else {
      timeoutId = setTimeout(() => {
        setDisplayText(currentWord.substring(0, displayText.length + 1));
      }, typingSpeed);
    }

    return () => clearTimeout(timeoutId);
  }, [
    displayText,
    isDeleting,
    currentWord,
    safeTexts,
    typingSpeed,
    deletingSpeed,
    pauseDuration,
    prefersReducedMotion,
  ]);

  useLayoutEffect(() => {
    const clip = clipRef.current;
    const probe = probeRef.current;
    if (!clip || !probe || !currentWord) {
      setScale(1);
      return undefined;
    }

    const measure = () => {
      probe.textContent = currentWord;
      const available = clip.clientWidth;
      const needed = probe.offsetWidth;
      // Keep a little room for the blinking cursor.
      const fit = available > 0 ? (available - 6) / needed : 1;
      setScale(fit < 1 ? fit : 1);
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [currentWord, className, style]);

  if (!safeTexts.length) return null;

  if (prefersReducedMotion) {
    return (
      <span className={`whitespace-nowrap ${className}`.trim()} style={style}>
        {safeTexts[0]}
      </span>
    );
  }

  return (
    <span className="absolute inset-0 z-0">
      <span
        ref={probeRef}
        className={`pointer-events-none absolute whitespace-nowrap opacity-0 ${className}`.trim()}
        style={{ ...style, left: 0, top: 0 }}
        aria-hidden
      />
      <span
        ref={clipRef}
        className="absolute inset-0 flex items-center justify-center overflow-hidden"
        aria-live="polite"
      >
        <span
          className="inline-flex max-w-full items-center justify-center"
          style={{
            transform: scale < 1 ? `scale(${scale})` : undefined,
            transformOrigin: "center center",
          }}
        >
          <span
            className={`typewriter-text whitespace-nowrap ${className}`.trim()}
            style={style}
          >
            {displayText}
          </span>
          {showCursor ? (
            <span className={cursorClassName} aria-hidden />
          ) : null}
        </span>
      </span>
    </span>
  );
}
